import axios from 'axios';
import type { LoginRequest, LoginResponse, RefreshTokenResponse, SessionCheckResponse, User } from '../types/auth';
import { API } from '../constants';
import { normalizeAccountDisplayName } from '../utils/accountName';

const AUTH_BASE_URL = `${API.AUTH_BASE}/auth`;

function mapUser(payload: any): User {
  return {
    id: payload.username,
    username: payload.username,
    displayName: normalizeAccountDisplayName({
      rawName: payload.displayName,
      username: payload.username,
      walletAddress: payload.walletAddress,
    }),
    email: payload.email || undefined,
    walletAddress: payload.walletAddress || undefined,
    roles: (payload.roles || []).map((r: string) => ({ id: r, name: r, permissions: [], description: r })),
    permissions: payload.permissions || [],
    createdAt: '',
    updatedAt: '',
  };
}

export const authService = {
  async login(data: LoginRequest): Promise<LoginResponse> {
    const response = await axios.post(`${AUTH_BASE_URL}/login`, data, { withCredentials: true });
    const d = response.data;
    return {
      expiresIn: 7200,
      user: mapUser(d),
    };
  },

  async refreshToken(): Promise<RefreshTokenResponse> {
    const response = await axios.post(`${AUTH_BASE_URL}/refresh`, {}, { withCredentials: true });
    const d = response.data || {};
    return {
      refreshed: d.refreshed !== false,
    };
  },

  async checkSession(): Promise<SessionCheckResponse> {
    const response = await axios.get(`${AUTH_BASE_URL}/check`, { withCredentials: true });
    const d = response.data || {};
    if (!d.valid) {
      return { valid: false };
    }
    return {
      valid: true,
      user: mapUser(d),
    };
  },

  async checkPermission(permission: string): Promise<boolean> {
    try {
      const session = await this.checkSession();
      return Boolean(session.valid && session.user?.permissions?.includes(permission));
    } catch {
      return false;
    }
  },

  async logout(): Promise<void> {
    try {
      await axios.post(`${AUTH_BASE_URL}/logout`, {}, { withCredentials: true });
    } catch {
      // Silently fail
    }
  },

  async submitRegistration(payload: {
    username: string;
    password: string;
    displayName?: string;
    requestedRole: 'consumer' | 'scenic' | 'market_maker' | 'exchange';
    contactJson?: string;
  }): Promise<{ success: boolean; applicationId?: number; status?: string; message?: string }> {
    try {
      const response = await axios.post(
        `${AUTH_BASE_URL}/registrations/apply`,
        payload,
        { withCredentials: false },
      );
      return response.data;
    } catch (error: any) {
      const data = error?.response?.data;
      return { success: false, message: data?.message || '提交失败，请稍后重试' };
    }
  },

  async listRegistrations(status?: 'PENDING' | 'PROVISIONING' | 'PROVISIONING_FAILED' | 'APPROVED' | 'REJECTED'): Promise<any[]> {
    const response = await axios.get(`${AUTH_BASE_URL}/registrations`, {
      params: status ? { status } : {},
      withCredentials: true,
    });
    return response.data || [];
  },

  async approveRegistration(id: number, scenicProfile?: {
    name: string;
    address?: string;
    description?: string;
    contactPhone?: string;
    contactEmail?: string;
  }): Promise<{ success: boolean; username?: string; address?: string; status?: string; message?: string }> {
    try {
      const response = await axios.post(
        `${AUTH_BASE_URL}/registrations/${id}/approve`,
        scenicProfile ? { scenicProfile } : {},
        { withCredentials: true },
      );
      return response.data;
    } catch (error: any) {
      return { success: false, message: error?.response?.data?.message || '批准失败' };
    }
  },

  async backfillScenicAccounts(): Promise<{
    success: boolean;
    completed?: number;
    skipped?: number;
    failed?: number;
    failures?: Array<{ username: string; message: string }>;
    message?: string;
  }> {
    try {
      const response = await axios.post(
        `${AUTH_BASE_URL}/registrations/scenic/backfill`,
        {},
        { withCredentials: true },
      );
      return response.data;
    } catch (error: any) {
      return { success: false, message: error?.response?.data?.message || '历史景区账户补齐失败' };
    }
  },

  async rejectRegistration(id: number, reason?: string): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await axios.post(
        `${AUTH_BASE_URL}/registrations/${id}/reject`,
        { reason },
        { withCredentials: true },
      );
      return response.data;
    } catch (error: any) {
      return { success: false, message: error?.response?.data?.message || '驳回失败' };
    }
  },
};
